import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLanguageStore, type Messages } from '@/i18n/i18n';
import { apiClient } from './apiClient';

// The organisation's interface languages (URS UX-02, docs §259): English is built in; each pack here adds one.

export interface UiLanguage { code: string; name: string; isActive: boolean; translated: number; updatedAt: string | null }

export function useUiLanguages(enabled = true) {
  return useQuery({
    queryKey: ['ui-languages'],
    queryFn: async () => (await apiClient.get<UiLanguage[]>('/api/v1/ui-languages')).data,
    enabled,
    staleTime: 10 * 60_000,
  });
}

export async function getUiLanguage(code: string): Promise<Messages> {
  return (await apiClient.get<Messages>(`/api/v1/ui-languages/${encodeURIComponent(code)}`)).data;
}

export function useUiLanguageMutations() {
  const qc = useQueryClient();
  const done = () => { qc.invalidateQueries({ queryKey: ['ui-languages'] }); };
  const save = useMutation({
    mutationFn: async (input: { code: string; name: string; isActive: boolean; messages: Messages }) =>
      (await apiClient.put<UiLanguage>(`/api/v1/ui-languages/${encodeURIComponent(input.code)}`, input)).data,
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: async (code: string) => { await apiClient.delete(`/api/v1/ui-languages/${encodeURIComponent(code)}`); }, onSuccess: done });
  return { save, remove };
}

/** Loads the person's chosen language once they are signed in; falls back to English when its pack is gone or switched off. */
export function useLanguageBootstrap(signedIn: boolean) {
  const code = useLanguageStore((s) => s.code);
  const setLanguage = useLanguageStore((s) => s.setLanguage);
  useEffect(() => {
    if (!signedIn || code === 'en') return;
    let cancelled = false;
    getUiLanguage(code).then((m) => { if (!cancelled) setLanguage(code, m); }).catch(() => { if (!cancelled) setLanguage('en', null); });
    return () => { cancelled = true; };
    // Once per sign-in: switching later loads the pack itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);
}
